import type { CouponRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';
import { expiryFromDay } from '../../expiryFromDay';
import { usePromotionBrandScope } from '../../usePromotionBrandScope';
import { invalidateAfterCouponMutation } from '../couponInvalidations';

const FORM_ID = 'edit-coupon-form';

const validationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  descriptionPublic: z.string(),
  expiresAt: z.date().nullish()
});

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  coupon: CouponRecord;
};

const getDefaultValues = (coupon: CouponRecord): FormValues => ({
  name: coupon.namePublic,
  descriptionPublic: coupon.descriptionPublic,
  expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt) : null
});

export const EditCouponSheet = ({ open, onClose, coupon }: Props) => {
  const { serviceOrganizationId, useStaffCouponWrite } = usePromotionBrandScope();

  const { mutateAsync: patchPartnerCoupon } = noddiAsync.usePatch({
    type: URLKeys.patchServiceOrganizationCoupon,
    queryConfig: {
      onSuccess: () => {
        void invalidateAfterCouponMutation({ couponId: coupon.id, serviceOrganizationId });
        onClose();
      }
    }
  });

  const { mutateAsync: patchAdminCoupon } = noddiAsync.usePatch({
    type: URLKeys.patchCoupon,
    queryConfig: {
      onSuccess: () => {
        void invalidateAfterCouponMutation({ couponId: coupon.id });
        onClose();
      }
    }
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(coupon),
    validators: { onSubmit: validationSchema },
    onSubmit: async ({ value }) => {
      const payload = {
        name: value.name.trim(),
        descriptionPublic: value.descriptionPublic.trim(),
        expiresAt: value.expiresAt ? expiryFromDay(value.expiresAt) : null
      };
      if (!useStaffCouponWrite && serviceOrganizationId != null) {
        await patchPartnerCoupon({ serviceOrganizationId, couponId: coupon.id, ...payload });
        return;
      }
      await patchAdminCoupon({ couponId: coupon.id, ...payload });
    }
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues(coupon));
    }
  }, [open, coupon, form]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit coupon'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppField name='name'>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack field render prop */}
            {(field: any) => <field.Input label='Name' required tooltip='Shown to the customer on the coupon.' />}
          </form.AppField>
          <form.AppField name='descriptionPublic'>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack field render prop */}
            {(field: any) => (
              <field.TextArea label='Description' rows={4} tooltip='Optional details the customer sees.' />
            )}
          </form.AppField>
          <form.AppField name='expiresAt'>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack field render prop */}
            {(field: any) => (
              <field.DatePicker
                label='Expires (optional)'
                tooltip='Last day the customer can use this coupon. Leave empty for no expiry.'
                showClearButton
              />
            )}
          </form.AppField>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
